<template>
    <div id="fleets-view" class="view">
        <settings-menu></settings-menu>

        <data-table
            v-if="!fetchingOrganization"
            :title="$t('listFleet.title')"
            :delete-button-text="$t('listFleet.buttons.delete.smallText')"
            :headers="headers"
            :get-items-handler="getFleetList"
            :get-delete-handler="deleteFleet"
            :excludes-deletable="excludesDeletable"
            :get-prepared-data-to-save="prepareDataToSave"
            :reset-filters-handler="resetFilters"
            :deletable="hasAllRoles(['FleetDelete'])"
            has-filters
        >
            <template v-slot:tableFilters>
                <fleets-filters
                    :filters="filters"
                ></fleets-filters>
            </template>
            <template v-slot:items="item">
                <td>
                    <template v-if="hasAllRoles(['FleetUpdate'])">
                        <router-link :to="{ name: 'editFleetDetails', params: { fleetUuid: item.props.item.id } }">
                            {{ item.props.item.name }}
                        </router-link>
                    </template>
                    <template v-else>
                        {{ item.props.item.name }}
                    </template>
                </td>
                <td>{{ item.props.item.organizationName }}</td>
                <td>{{ item.props.item.allocatedDevices }}</td>
                <td>{{ item.props.item.unallocatedDevices }}</td>
                <td>{{ item.props.item.totalDevices }}</td>
                <td v-if="hasAllRoles(['FleetUpdate'])" class="text-center">
                    <router-link :to="{ name: 'editFleetDetails', params: { fleetUuid: item.props.item.id } }">
                        <sp-button design-type="table-button" icon="edit"></sp-button>
                    </router-link>
                </td>
            </template>

            <template v-slot:createButton v-if="hasAllRoles(['FleetAdd'])">
                <router-link to="/settings/fleets/create" class="no-underline">
                    <sp-button sp-class="no-padding-right" icon="car" :text="$t('listFleet.buttons.create.text')"></sp-button>
                </router-link>
            </template>
        </data-table>
        <sp-loader v-if="isLoading"></sp-loader>
    </div>
</template>

<script>
import SettingsMenu from '@/components/layout/SettingsMenu'
import SpButton from '@/components/forms/Button'
import DataTable from '@/views/_components/DataTable'
import FleetsFilters from '@/views/Fleets/components/FleetsFilters'
import FleetApi from '@/api/FleetApi'

import SpLoader from '@/components/atoms/Loader'
import toast from '@/plugins/izitoast'
import { mapGetters, mapActions } from 'vuex'
import OrganizationApi from '@/api/OrganizationApi'
import { setTableFiltersToLocalStorage, getTableFiltersFromLocalStorage } from '@/helpers/StorageHelper'

const body = document.body
const fleetApi = new FleetApi()
const organizationApi = new OrganizationApi()

export default {
    name: 'Fleets',
    mounted () {
        body.classList.add('devices-view')
        this.applySavedFilters()
        if (this.fleet) {
            this.filters.fleet = this.fleet
        }
    },
    beforeDestroy () {
        body.classList.remove('devices-view')
    },
    components: {
        DataTable,
        SettingsMenu,
        SpButton,
        FleetsFilters,
        SpLoader
    },
    data () {
        return {
            headers: [
                {
                    text: this.$t('listFleet.table.headers.name'),
                    sortable: true,
                    value: 'name'
                },
                {
                    text: this.$t('listFleet.table.headers.organizationName'),
                    sortable: false,
                    value: 'organizationName'
                },
                {
                    text: this.$t('listFleet.table.headers.allocatedDevices'),
                    sortable: false,
                    value: 'allocatedDevices'
                },
                {
                    text: this.$t('listFleet.table.headers.unallocatedDevices'),
                    sortable: false,
                    value: 'unallocatedDevices'
                },
                {
                    text: this.$t('listFleet.table.headers.totalDevices'),
                    sortable: false,
                    value: 'totalDevices'
                },
                {
                    text: this.$t('listFleet.table.headers.edit'),
                    sortable: false,
                    value: 'isEditable',
                    width: '70px',
                    class: !this.hasAllRoles(['FleetUpdate']) ? 'd-none' : ''
                },
                {
                    text: this.$t('listFleet.table.headers.delete'),
                    sortable: false,
                    value: 'isDeletable',
                    width: '70px',
                    class: !this.hasAllRoles(['FleetDelete']) ? 'd-none' : ''
                }
            ],
            excludesDeletable: [
                {
                    identifier: process.env.VUE_APP_ROOT_FLEET_IDENTIFIER,
                    value: process.env.VUE_APP_ROOT_FLEET_VALUE
                }
            ],
            totalResults: 0,
            isLoading: false,
            fetchingOrganization: false,
            fetchingFilters: false,
            filters: {
                organization: null,
                fleet: null,
                users: null
            }
        }
    },
    computed: {
        ...mapGetters('authModule', [
            'getActiveOrganization',
            'getIsImpersonateActive',
        ]),
        ...mapGetters('managementTables', [
            'getFleetFilters'
        ]),
        isFleetTableReady () {
            return !this.fetchingOrganization && !this.fetchingFilters
        }
    },
    methods: {
        ...mapActions('managementTables', [
            'setFleetFilters'
        ]),
        async getFleetList (page, rowsPerPage, search, sortBy) {
            this.isLoading = true

            const response = await fleetApi.getFleetList({
                fleetName: this.filters.fleet ? this.filters.fleet.name : null,
                organization: this.filters.organization ? this.filters.organization : null,
                users: this.filters.users ? this.filters.users : null,
                page: page,
                perPage: rowsPerPage,
                sortBy: sortBy
            })
            this.totalResults = response.payload.totalResults

            if (!this.getIsImpersonateActive) {
                this.setFleetFilters(this.filters)
                setTableFiltersToLocalStorage('fleets', this.filters)
            }

            this.isLoading = false
            return {
                total: response.payload.totalResults,
                items: response.payload.items
            }
        },
        async applySavedFilters () {
            if (!this.getIsImpersonateActive) {
                const savedFilters = getTableFiltersFromLocalStorage('fleets')
                if (savedFilters) {
                    this.filters = { ...savedFilters }
                    return
                }
            } else {
                this.filters.organization = null
                this.filters.fleet = null
                this.filters.users = null
            }
            await this.getOrganization()
        },
        async getOrganization () {
            this.fetchingOrganization = true
            const response = await organizationApi.getOrganizationById(this.getActiveOrganization)
            if (!response.isSuccess) {
                this.fetchingOrganization = false
                return
            }
            this.filters.organization = response.payload
            this.fetchingOrganization = false
        },
        async deleteFleet (fleet) {
            const response = await fleetApi.deleteFleet(fleet.id)

            if (!response.isSuccess) {
                return
            }

            toast.success(`${this.$t('listFleet.messages.delete.success.text1')} ${fleet.name} ${this.$t('listFleet.messages.delete.success.text2')}`)
        },
        async prepareDataToSave (search) {
            const dataToSave = []
            let items = []

            const response = await fleetApi.getFleetList({
                fleetName: this.filters.fleet ? this.filters.fleet.name : null,
                organization: this.filters.organization ? this.filters.organization : null,
                users: this.filters.users ? this.filters.users : null
            })
            items = items.concat(response.payload.items)

            items.forEach(item => {
                dataToSave.push({
                    name: item.name,
                    allocatedDevices: '' + item.allocatedDevices,
                    unallocatedDevices: '' + item.unallocatedDevices,
                    totalDevices: '' + item.totalDevices
                })
            })
            return dataToSave
        },
        resetFilters () {
            this.filters = {
                organization: this.getIsImpersonateActive ? this.filters.organization : null,
                fleet: null
            }
        }
    }
}
</script>

<style scoped>

</style>
